import React, { useState, useCallback, useMemo } from 'react';
import {
    ChevronRight,
    ChevronDown,
    Folder,
    FolderOpen,
    FileText,
    Activity,
    Layers,
    Search,
    X,
    RefreshCw,
} from 'lucide-react';
import type { ExpenseActivityTreeNode, ActivityTreeNodeType } from '../types';

interface ExpenseActivityTreeProps {
    data: ExpenseActivityTreeNode[];
    isLoading?: boolean;
    onNodeClick?: (node: ExpenseActivityTreeNode) => void;
    onNavigate?: (path: string, node: ExpenseActivityTreeNode) => void;
}

interface TreeNodeProps {
    node: ExpenseActivityTreeNode;
    level: number;
    expandedNodes: Set<number>;
    onToggle: (nodeId: number) => void;
    onNodeClick?: (node: ExpenseActivityTreeNode) => void;
    onNavigate?: (path: string, node: ExpenseActivityTreeNode) => void;
    searchQuery: string;
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 5,
    }).format(amount);
};

// Get icon based on node type
const getNodeIcon = (type: ActivityTreeNodeType, isExpanded: boolean) => {
    switch (type) {
        case 'head':
            return isExpanded ? (
                <FolderOpen className="h-5 w-5 text-yellow-500" />
            ) : (
                <Folder className="h-5 w-5 text-yellow-500" />
            );
        case 'subHead':
            return isExpanded ? (
                <FolderOpen className="h-4 w-4 text-yellow-500" />
            ) : (
                <Folder className="h-4 w-4 text-yellow-500" />
            );
        case 'activity':
            return <FileText className="h-4 w-4 text-gray-500" />;
        case 'subActivity':
            return <FileText className="h-4 w-4 text-gray-400" />;
        default:
            return <Layers className="h-4 w-4 text-gray-500" />;
    }
};

// Get background and border colors based on node type
const getNodeStyles = (type: ActivityTreeNodeType) => {
    const baseStyles = 'transition-all duration-200 rounded-lg bg-gray-50';

    switch (type) {
        case 'head':
            return `${baseStyles} border-l-4 border-l-red-500`;
        case 'subHead':
            return `${baseStyles} border-l-4 border-l-yellow-500`;
        case 'activity':
            return `${baseStyles} border-l-4 border-l-green-500`;
        case 'subActivity':
            return `${baseStyles} border-l-4 border-l-purple-500`;
        default:
            return `${baseStyles} border-l-4 border-gray-400`;
    }
};

// Get type badge
const getTypeBadge = (type: ActivityTreeNodeType) => {
    const badges: Record<ActivityTreeNodeType, { label: string; className: string }> = {
        head: { label: 'HEAD', className: 'bg-red-500 text-white' },
        subHead: { label: 'SUBHEAD', className: 'bg-yellow-500 text-white' },
        activity: { label: 'ACTIVITY', className: 'bg-green-500 text-white' },
        subActivity: { label: 'SUBACTIVITY', className: 'bg-purple-500 text-white' },
    };

    const badge = badges[type];
    if (!badge) return null;
    
    return (
        <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full ${badge.className}`}>
            {badge.label}
        </span>
    );
};

// Highlight search text in title
const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 px-0.5 rounded">
                {part}
            </mark>
        ) : (
            part
        )
    );
};

// Check if node or any child matches search
const nodeMatchesSearch = (node: ExpenseActivityTreeNode, query: string): boolean => {
    if (!query.trim()) return true;

    const lowerQuery = query.toLowerCase();
    if (node.title.toLowerCase().includes(lowerQuery)) return true;

    return node.children.some((child) => nodeMatchesSearch(child, query));
};

// Tree Node Component
const TreeNode: React.FC<TreeNodeProps> = ({
    node,
    level,
    expandedNodes,
    onToggle,
    onNodeClick,
    onNavigate,
    searchQuery,
}) => {
    // const [isHovered, setIsHovered] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);

    // Filter visible children based on search
    const visibleChildren = useMemo(() => {
        if (!searchQuery.trim()) return node.children;
        return node.children.filter((child) => nodeMatchesSearch(child, searchQuery));
    }, [node.children, searchQuery]);

    // Check if this node matches search
    const matchesSearch =
        !searchQuery.trim() || node.title.toLowerCase().includes(searchQuery.toLowerCase());

    // Don't render if doesn't match and has no matching children
    if (searchQuery.trim() && !matchesSearch && visibleChildren.length === 0) {
        return null;
    }

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) {
            onToggle(node.id);
        }
    };

    const handleNodeClick = () => {
        if (onNodeClick) {
            onNodeClick(node);
        }
    };

    const paddingLeft = level * 24;

    return (
        <div className="animate-fadeIn relative">
            <div className="flex relative">
                {/* Visual Guide Line for hierarchy */}
                {level > 0 && (
                    <div
                        className="absolute left-0 top-0 bottom-0 border-l border-dashed border-gray-300"
                        style={{ left: paddingLeft - 12 }}
                    />
                )}

                <div className="flex-1 min-w-0" style={{ marginLeft: paddingLeft }}>
                    {/* Node Card */}
                    <div
                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 cursor-pointer ${getNodeStyles(node.type)} mb-2 relative hover:shadow-md transition-shadow`}
                        // onMouseEnter={() => setIsHovered(true)}
                        // onMouseLeave={() => setIsHovered(false)}
                        onClick={handleNodeClick}
                    >
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                            {/* Expand/Collapse Button */}
                            <button
                                onClick={handleToggle}
                                className={`flex-shrink-0 mt-1 p-0.5 rounded hover:bg-black/5 transition-colors ${hasChildren ? 'visible' : 'invisible'
                                    }`}
                            >
                                {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                            </button>

                            {/* Icon */}
                            <div className="flex-shrink-0 mt-1">{getNodeIcon(node.type, isExpanded)}</div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-medium text-gray-800 break-words">
                                        {highlightText(node.title, searchQuery)}
                                    </span>
                                    <div className="flex-shrink-0 ml-auto sm:ml-2">
                                        {getTypeBadge(node.type)}
                                    </div>
                                </div>

                                {/* Financial Data */}
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm">
                                    <div className="flex items-center gap-1 text-orange-600">
                                        <span className="text-xs font-medium text-gray-500">Sanctioned:</span>
                                        <span className="font-semibold">{formatCurrency(node.totalSanctioned)}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-green-600">
                                        <span className="text-xs font-medium text-gray-500" title="Sanctioned amount of heads marked Continue — not funds released">Active Sanc:</span>
                                        <span className="font-semibold">{formatCurrency(node.activeSanctionedAmount)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Children */}
            {isExpanded && hasChildren && (
                <div className="relative">
                    {visibleChildren.map((child) => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            level={level + 1}
                            expandedNodes={expandedNodes}
                            onToggle={onToggle}
                            onNodeClick={onNodeClick}
                            onNavigate={onNavigate}
                            searchQuery={searchQuery}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Main Expense Activity Tree Component
const ExpenseActivityTree: React.FC<ExpenseActivityTreeProps> = ({
    data,
    isLoading = false,
    onNodeClick,
    onNavigate,
}) => {
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set([1])); // Default expand first node if it exists
    const [searchQuery, setSearchQuery] = useState('');

    // Toggle node expansion
    const handleToggle = useCallback((nodeId: number) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    }, []);

    // Expand all nodes
    const expandAll = useCallback(() => {
        const allIds = new Set<number>();
        const collectIds = (nodes: ExpenseActivityTreeNode[]) => {
            nodes.forEach((node) => {
                if (node.children && node.children.length > 0) {
                    allIds.add(node.id);
                    collectIds(node.children);
                }
            });
        };
        collectIds(data);
        setExpandedNodes(allIds);
    }, [data]);

    // Collapse all nodes
    const collapseAll = useCallback(() => {
        setExpandedNodes(new Set());
    }, []);

    // Expand nodes that match search
    const expandMatching = useCallback(() => {
        if (!searchQuery.trim()) return;

        const matchingIds = new Set<number>();
        const findMatching = (nodes: ExpenseActivityTreeNode[], parentIds: number[] = []) => {
            nodes.forEach((node) => {
                const newPath = [...parentIds, node.id];
                if (node.title.toLowerCase().includes(searchQuery.toLowerCase())) {
                    parentIds.forEach((id) => matchingIds.add(id));
                }
                if (node.children && node.children.length > 0) {
                    findMatching(node.children, newPath);
                }
            });
        };
        findMatching(data);
        setExpandedNodes(matchingIds);
    }, [data, searchQuery]);

    // Filter visible root nodes
    const visibleRoots = useMemo(() => {
        if (!searchQuery.trim()) return data;
        return data.filter((node) => nodeMatchesSearch(node, searchQuery));
    }, [data, searchQuery]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
                <span className="ml-3 text-gray-600">Loading expense activity tree...</span>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="p-1 bg-gradient-to-br from-blue-500 to-indigo-600 rounded text-white"><Activity className="h-5 w-5" /></span>
                    Expense Activity Tree
                </h2>
            </div>

            {/* Search & Controls */}
            <div className="p-4 border-b border-gray-200 bg-gray-50/50">
                <div className="flex flex-col sm:flex-row gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search expenses..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (e.target.value) expandMatching();
                            }}
                            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <X className="h-4 w-4 text-gray-500" />
                            </button>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={expandAll}
                            className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors flex items-center gap-1"
                        >
                            <ChevronDown className="h-4 w-4" />
                            Expand
                        </button>
                        <button
                            onClick={collapseAll}
                            className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors flex items-center gap-1"
                        >
                            <ChevronRight className="h-4 w-4" />
                            Collapse
                        </button>
                    </div>
                </div>
            </div>

            {/* Tree Content */}
            <div className="p-4 max-h-[700px] overflow-y-auto">
                {visibleRoots.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="font-medium">No expenses found</p>
                        <p className="text-sm">Try a different search term</p>
                    </div>
                ) : (
                    visibleRoots.map((node) => (
                        <TreeNode
                            key={node.id}
                            node={node}
                            level={0}
                            expandedNodes={expandedNodes}
                            onToggle={handleToggle}
                            onNodeClick={onNodeClick}
                            onNavigate={onNavigate}
                            searchQuery={searchQuery}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default ExpenseActivityTree;
